import { defaultLocale, SupportedLocale } from './index';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['fr', 'en', 'es', 'pt', 'nl'];

export const LOCALE_STORAGE_KEY = 'app_locale';

export const RESERVED_PATH_SEGMENTS = new Set([
  'fr',
  'en',
  'es',
  'pt',
  'nl',
  'auth',
  'dashboard',
  'equipment',
  'cgu',
  'privacy-policy',
]);

export function isSupportedLocale(value: string | undefined | null): value is SupportedLocale {
  return value === 'fr' || value === 'en' || value === 'es' || value === 'pt' || value === 'nl';
}

export function localizePath(locale: SupportedLocale, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (clean === '/') {
    return `/${locale}`;
  }
  return `/${locale}${clean}`;
}

export function getLocaleFromPath(pathname: string): SupportedLocale | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  return isSupportedLocale(segment) ? segment : null;
}

export function stripLocale(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && isSupportedLocale(parts[0])) {
    const rest = parts.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  if (!pathname) {
    return '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function swapLocaleInPath(pathname: string, nextLocale: SupportedLocale): string {
  return localizePath(nextLocale, stripLocale(pathname));
}

export function getStoredLocale(): SupportedLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function setStoredLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore storage errors
  }
}

export function resolveOfflineLocale(): SupportedLocale {
  const stored = getStoredLocale();
  if (stored) {
    return stored;
  }
  if (typeof navigator !== 'undefined') {
    const lang = navigator.language?.slice(0, 2).toLowerCase();
    if (isSupportedLocale(lang)) {
      return lang;
    }
  }
  return defaultLocale;
}

export function isReservedObjCode(objCode: string): boolean {
  return RESERVED_PATH_SEGMENTS.has(objCode.toLowerCase());
}
